import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, useTeams, useTeamMembers } from '../hooks';
import {
  TeamInvitesManager,
  TeamGeneralSettings,
  TeamMembersList,
  DeleteTeamSection,
} from '../components/teams';
import { EmptyState, Loading, LimitIndicator, SessionUnavailable } from '../components/common';
import { ROUTES } from '../routes';
import type { TeamSummary } from '@mockd/shared';

export function SettingsTeam() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, sessionError } = useAuth();
  const { getTeam, updateTeam, deleteTeam } = useTeams();
  const { members, loading: membersLoading, fetchMembers, updateMemberRole, removeMember } = useTeamMembers();

  const [team, setTeam] = useState<TeamSummary | null>(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user && !sessionError) {
      navigate('/login', { replace: true, state: { from: { pathname: `/teams/${teamId}/settings` } } });
    }
  }, [user, authLoading, sessionError, teamId, navigate]);

  useEffect(() => {
    if (!teamId || !user) return;
    setTeamLoading(true);
    getTeam(teamId)
      .then((t) => {
        setTeam(t);
        setTeamError(null);
      })
      .catch((err) => {
        setTeamError(err instanceof Error ? err.message : 'Failed to load team');
      })
      .finally(() => setTeamLoading(false));
  }, [teamId, user, getTeam]);

  useEffect(() => {
    if (teamId && user) fetchMembers(teamId);
  }, [teamId, user, fetchMembers]);

  if (!authLoading && sessionError) {
    return <SessionUnavailable />;
  }

  if (authLoading || !user || !teamId || teamLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (teamError || !team) {
    return (
      <div className="flex-1 bg-base-200 p-6">
        <div className="max-w-3xl mx-auto">
          <EmptyState
            as="h1"
            title="Team not found"
            description={teamError || "This team doesn't exist, or you're not a member."}
            action={
              <Link to={ROUTES.dashboard()} className="btn btn-primary btn-sm">
                Go to your projects
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const canManage = team.role === 'owner' || team.role === 'admin';
  const isOwner = team.role === 'owner';
  // The same count the seat indicator shows, and the one the server checks on send.
  const seatsUsed = team.memberCount + team.pendingInviteCount;

  // Re-read the seat counts after anything that frees or takes a seat. The server
  // owns them (it drops expired invites), so don't recompute them here. On failure
  // the stale counts stay, and the server still rejects a send over the cap.
  const refreshTeam = () => {
    getTeam(team.id).then(setTeam).catch(() => {});
  };

  return (
    <div className="flex-1 bg-base-200 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <h1>{team.name} settings</h1>

        <TeamGeneralSettings
          team={team}
          canEdit={canManage}
          onRename={async (name) => {
            await updateTeam(team.id, { name });
            setTeam((prev) => (prev ? { ...prev, name } : prev));
          }}
        />

        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Members</h2>
            <div className="mb-4">
              <LimitIndicator
                used={seatsUsed}
                max={team.seatLimit}
                label="Seats"
              />
            </div>
            <TeamMembersList
              members={members}
              loading={membersLoading}
              currentUserId={user.id}
              ownerUserId={team.ownerUserId}
              canManage={canManage}
              onChangeRole={(userId, role) => updateMemberRole(team.id, userId, role)}
              onRemove={async (userId) => {
                await removeMember(team.id, userId);
                refreshTeam();
              }}
            />
          </div>
        </div>

        {canManage && (
          <div className="card bg-base-100">
            <div className="card-body">
              <h2 className="card-title text-lg">Invites</h2>
              <TeamInvitesManager
                teamId={team.id}
                seatLimit={team.seatLimit}
                atSeatLimit={seatsUsed >= team.seatLimit}
                onChange={refreshTeam}
              />
            </div>
          </div>
        )}

        {isOwner && (
          <DeleteTeamSection
            teamName={team.name}
            onDelete={async () => {
              await deleteTeam(team.id);
              navigate(ROUTES.dashboard(), { replace: true });
            }}
          />
        )}
      </div>
    </div>
  );
}
